import { useMemo } from 'react';
import { type Product, type SaleMode } from '../types';

/**
 * Hook for efficient product lookups with memoization.
 *
 * Uses a Map for O(1) lookups instead of O(n) array.find() operations.
 * The Map is memoized and only rebuilds when the products array changes.
 *
 * @param products - Array of products to create lookups from
 * @returns Object with helper functions for product lookups
 *
 * @example
 * const { getProduct, getName, getSection } = useProductLookup(products);
 * const productName = getName(productId); // Fast O(1) lookup
 */
export function useProductLookup(products: Product[]) {
  // Create a Map for O(1) lookups - only rebuilds when products array changes
  const productMap = useMemo(
    () => new Map(products.map((p) => [p.id, p])),
    [products]
  );

  return useMemo(
    () => ({
      /**
       * Get full product by ID
       * @param id - Product ID
       * @returns Product object or undefined if not found
       */
      getProduct: (id: string): Product | undefined => {
        return productMap.get(id);
      },

      /**
       * Get product name by ID
       * @param id - Product ID
       * @returns Product name or the ID itself if product not found
       */
      getName: (id: string): string => {
        return productMap.get(id)?.name || id;
      },

      /**
       * Get product sale mode by ID
       * @param id - Product ID
       * @returns Sale mode, defaults to 'UNIT' if not found
       */
      getSaleMode: (id: string): SaleMode => {
        return productMap.get(id)?.saleMode || 'UNIT';
      },

      /**
       * Get product price by ID
       * @param id - Product ID
       * @returns Product price or undefined if product not found or has no price
       */
      getPrice: (id: string): number | undefined => {
        return productMap.get(id)?.price;
      },

      /**
       * Check if product exists
       * @param id - Product ID
       * @returns true if product exists
       */
      exists: (id: string): boolean => {
        return productMap.has(id);
      },

      /**
       * Get product section by ID
       * @param id - Product ID
       * @returns Product section or undefined if product not found or has no section
       */
      getSection: (id: string): string | undefined => {
        return productMap.get(id)?.section;
      },
    }),
    [productMap]
  );
}
